import React, { useEffect, useState, useRef } from 'react';
import Sidebar from 'components/Sidebar/Sidebar';
import DashboardNavbar from 'components/Navbars/DashboardNavbar';
import CustomLink from 'components/CustomLink/CustomLink';
import CustomButton from 'components/CustomButtons/Button';
import { Breadcrumbs } from 'react-breadcrumbs-dynamic';
import { AuthConsumer } from 'components/Auth/authContext';
import AuthHOC from 'layouts/AuthHOC';
import { dashRoutes } from 'routes/dashboardRoutes';
import styles from 'assets/jss/material-dashboard-pro-react/layouts/adminStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { Switch, Redirect, Route, useHistory } from 'react-router-dom';
import { dynamicTranslate, translate } from 'components/Translation';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import { throwError } from 'rxjs';
import { AccountsService } from 'services/accounts/accounts';
import { BodyM, BodyMBold, HeadingM, HeadingMBold } from 'components/Typography/Typography';
import { getLanguage } from 'utils/language/language';
import { fetchLabels } from 'utils/labels/labels';

import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from 'redux/modules/settings/language/actions';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import Footer from './subcomponents/Footer';
import { AuthorizationService } from 'services';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { setAllAccountDetails, setSelectedAccountDetails } from 'redux/modules/addServices/actions';
import PaymentResult from 'components/PaymentResult';
import Help from '@material-ui/icons/Help';
import { isAlertsEnabled, isUserGuideOn } from 'utils/killswitch';
import { UserGuideURL } from 'config/app.config';
import OldFooter from './subcomponents/OldFooter';
import PageContainer from './subcomponents/PageContainer/PageContainer';
import PageHeader from './subcomponents/PageHeader/PageHeader';
import AlertBar from './subcomponents/AlertBar/AlertBar';
import SearchBar from './subcomponents/SearchBar/SearchBar';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';

import ChangePasswordDialog, { Form } from 'views/Auth/ChangePasswordDialog';
import { isLoggedInSelector, isWeakPassword, userNameSelector } from 'redux/modules/session/selectors';
import { getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
// @ts-ignore
const useStyles = makeStyles(styles);

interface IDashboardProps {
	userType: string;
	location: {
		pathname: string;
	};
}

const Dashboard = ({ userType, location }: IDashboardProps) => {
	const [mobileOpen, setMobileOpen] = useState<boolean>(false);
	const [miniEnabled, setMiniEnabled] = useState<boolean>(false);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const dispatch = useDispatch();
	const theme = useTheme();
	const history = useHistory();
	const mainPanel = useRef<HTMLDivElement>(null);
	const breadcrumbsVisible: boolean = useMediaQuery(theme.breakpoints.up('sm'));
	const isEnterprise: boolean = accounts ? accounts.length > 1 : false;
	const changeLanguageTo: types.redux.settings.language = getLanguage() === 'ar' ? 'en' : 'ar';
	const remoteConfig = useSelector(remoteConfigSelector);
	const isUserGuideEnabled = isUserGuideOn(remoteConfig);
	const isDashboardAlertsEnabled = isAlertsEnabled(remoteConfig);
	const isNewHeaderVisible = useNewTheme();
	const classes = useStyles({ isNewHeaderVisible });
	const isUserLoggedIn = useSelector(isLoggedInSelector);
	const isUserPasswordWeak = useSelector(isWeakPassword);
	const userName = useSelector(userNameSelector);
	const commonLabels = useSelector(getCommonLabels);
	const { pathname } = location;

	const mainPanelClasses = classNames({
		[classes.mainPanel]: true,
		[classes.mainPanelSidebarMini]: miniEnabled,
	});

	const sidebarMinimize = () => {
		setMiniEnabled((enabled) => !enabled);
	};

	const handleDrawerToggle = () => {
		setMobileOpen((open) => !open);
	};

	const closeSidebar = () => {
		setMobileOpen(false);
	};

	const mapViews = (views: types.routes.ISingleDashboardRoute[]) => {
		return views.map((view) => ({
			...view,
			name: translate(view.name),
		}));
	};

	const navigateToUserGuide = () => history.push('/dashboard/help');

	const routes = dashRoutes({ accountType: isEnterprise ? 'ENTERPRISE' : 'CORPORATE', userType, remoteConfig })
		.filter((route) => {
			return route.visible;
		})
		.map((route) => ({
			...route,
			name: translate(route.name),
			views: mapViews(route?.views || []),
		}));

	const getRoutes = (routes: types.routes.ISingleDashboardRoute[], pathPrefix = '/dashboard'): JSX.Element[] => {
		return routes
			.filter((prop) => {
				return prop.layout === '/dashboard';
			})
			.map((prop) => {
				let path = `${pathPrefix}${prop.path}`;
				if (prop.customPath) {
					return <></>;
				}
				if (prop.params) {
					path += prop.params;
				}
				if (prop.views) {
					return getRoutes(prop.views, path);
				}
				const Component: any = prop.component;
				return (
					<Route
						path={path}
						key={path}
						render={(props) =>
							isUserLoggedIn && isUserPasswordWeak ? (
								<Redirect to="/auth/forgot-page" />
							) : (
								<Component mainContainerRef={mainPanel} {...props} />
							)
						}
					/>
				);
			})
			.flat();
	};

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser().subscribe(
			({ accounts }) => {
				setAccounts(accounts);
				/** set selected organization in redux store, the documentId of which will be used while 
				calling submit API for adding new micro bundle and activating services 
				checking if the accounts.length>0 so that there is no code break*/
				if (accounts && accounts.length > 0) {
					dispatch(setSelectedAccountDetails(accounts[0]));
					dispatch(setAllAccountDetails(accounts));
				}
			},
			(error) => {
				AuthorizationService.logOut().subscribe(
					() => {},
					() => {}
				);
				throwError(error);
			}
		);
	}, []);
	const accountType = isEnterprise ? 'ENTERPRISE' : 'CORPORATE';
	return (
		<div className={classes.wrapper}>
			<Route render={(props) => (!/\/gift/.test(props.location.pathname) ? <PaymentResult /> : <></>)} />
			{!isNewHeaderVisible && (
				<Sidebar
					routes={routes}
					image="/background-sidebar-dark.svg"
					handleDrawerToggle={handleDrawerToggle}
					open={mobileOpen}
					color="primary"
					bgColor="gray"
					miniEnabled={miniEnabled}
					pathname={pathname}
					onLinkClick={closeSidebar}
				/>
			)}

			{isNewHeaderVisible ? (
				<div ref={mainPanel}>
					<PageHeader
						userType={userType}
						remoteConfig={remoteConfig}
						accountType={accountType}
						pathname={pathname}
					/>
					<SearchBar>
						{location.pathname === '/dashboard/summary' ? (
							<HeadingM>{dynamicTranslate(commonLabels.dashboardGreeting, userName)}</HeadingM>
						) : (
							<Breadcrumbs
								container={({ children }: any) => <div role="navigation">{children}</div>}
								separator={
									<BodyM inline color="secondary-light">
										{' / '}
									</BodyM>
								}
								item={(props: any) => (
									<CustomLink
										type="bodyM"
										color="brand"
										{...props}
										isNewDashboardEnabled={isNewHeaderVisible}
									/>
								)}
								finalItem={(props: any) => (
									<BodyM inline color="primary-light">
										{props.children}
									</BodyM>
								)}
							/>
						)}
					</SearchBar>
					{isDashboardAlertsEnabled && <AlertBar />}
					<main className={classes.content}>
						<PageContainer>
							<Switch>
								{getRoutes(
									dashRoutes({
										accountType,
										userType,
										remoteConfig,
									})
								)}
								<Redirect exact from="/dashboard" to="/dashboard/summary" />
								<Redirect to="/dashboard/error-page" />
							</Switch>
						</PageContainer>
					</main>
					<Footer />
				</div>
			) : (
				<div className={mainPanelClasses} ref={mainPanel}>
					<div className={classes.languageSwitch}>
						<div>
							<div>
								{isUserGuideEnabled && (
									<CustomButton
										href={UserGuideURL}
										target="_blank"
										className={classes.languageSwitchButton}
										color="tertiary-light"
									>
										<>
											<Help className={classes.helpIcon} />
											{translate(`help`)}
										</>
									</CustomButton>
								)}
								<CustomButton
									className={classes.languageSwitchButton}
									color="tertiary-light"
									onClick={() => {
										fetchLabels(changeLanguageTo);
										dispatch(setLanguage(changeLanguageTo));
									}}
								>
									{translate(`auth.${changeLanguageTo}`)}
								</CustomButton>
								<div className={classes.languageSwitchDivider} />
								<BodyMBold color="secondary-light" inline disableMargin>
									{translate('login.enterprise')}
								</BodyMBold>
							</div>
						</div>
					</div>
					<DashboardNavbar
						sidebarMinimize={sidebarMinimize}
						miniActive={miniEnabled}
						brandNode={
							breadcrumbsVisible ? (
								<Breadcrumbs
									separator={
										<BodyM inline color="secondary-light">
											{' / '}
										</BodyM>
									}
									item={(props: any) => <CustomLink type="bodyM" color="brand" {...props} />}
									finalItem={(props: any) => (
										<BodyM inline color="secondary-light">
											{props.children}
										</BodyM>
									)}
								/>
							) : (
								undefined
							)
						}
						handleDrawerToggle={handleDrawerToggle}
					/>
					<div className={classes.content}>
						<div className={classes.container}>
							<Switch>
								{getRoutes(
									dashRoutes({
										accountType: isEnterprise ? 'ENTERPRISE' : 'CORPORATE',
										userType,
										remoteConfig,
									})
								)}
								<Redirect exact from="/dashboard" to="/dashboard/summary" />
								<Redirect to="/dashboard/error-page" />
							</Switch>
						</div>
					</div>
					<OldFooter />
				</div>
			)}
		</div>
	);
};

const DashboardCan = (props: any) => {
	return <AuthConsumer>{({ user }) => <Dashboard userType={user.userType} {...props} />}</AuthConsumer>;
};

export default AuthHOC(DashboardCan);
